import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, X, Star } from 'lucide-react';
import AgeVerificationGate from '../components/explicit/AgeVerificationGate';
import VideoCard from '../components/explicit/VideoCard';
import ExplicitHeroHeader from '../components/explicit/ExplicitHeroHeader';
import SubmitTipButton from '../components/SubmitTipButton';
import SiteFooter from '../components/sections/SiteFooter';
import TopNav from '../components/sections/TopNav';
import AdSlot from '../components/AdSlot';
import { useJsonLd, buildVideoObject, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';

const ExplicitContent = () => {
  const [ageVerified, setAgeVerified] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Inject one VideoObject schema for the most recent 10 videos. Helps Google
  // Video Search surface the gallery (rank in /videos vertical).
  useJsonLd(
    videos.length > 0
      ? {
          '@context': 'https://schema.org',
          '@graph': videos.slice(0, 10).map(buildVideoObject),
        }
      : null,
    'explicit-videos',
  );
  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'Explicit Footage', url: 'https://www.outlawmedia.com.au/explicit' },
  ]), 'explicit-breadcrumb');

  const fetchVideos = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos`);
      const data = await response.json();
      setVideos(data);
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let granted = false;
    try {
      const raw = localStorage.getItem('outlaw_age_grant');
      if (raw) {
        const grant = JSON.parse(raw);
        if (grant && grant.verified && grant.expiresAt && grant.expiresAt > Date.now()) {
          granted = true;
        } else {
          localStorage.removeItem('outlaw_age_grant');
        }
      }
    } catch (e) {
      console.warn('Age grant parse failed, falling through to gate:', e);
    }
    if (!granted && sessionStorage.getItem('age_verified') === 'true') {
      granted = true;
    }

    if (granted) {
      setAgeVerified(true);
      fetchVideos();
    } else {
      setLoading(false);
    }
  }, [fetchVideos]);

  const handleAgeVerification = (confirmed) => {
    if (confirmed) {
      sessionStorage.setItem('age_verified', 'true');
      setAgeVerified(true);
      fetchVideos();
    } else {
      navigate('/');
    }
  };

  if (!ageVerified) {
    return <AgeVerificationGate onVerify={handleAgeVerification} />;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="explicit-page">
      <TopNav />
      <PageSeoTag pageKey="explicit" />
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-black text-white">EXPLICIT CONTENT</h1>
              <p className="text-red-400 text-sm font-medium">18+ Restricted Section</p>
            </div>
            <button
              onClick={() => navigate('/')}
              data-testid="explicit-exit-btn"
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Exit Section
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading content...</div>
          </div>
        ) : (
          <>
            <ExplicitHeroHeader />
            {videos.length === 0 ? (
              <section className="relative mb-12" data-testid="explicit-empty-state">
                {/* Gold glow background - mirrors FeaturedCarousel */}
                <div className="absolute inset-0 -z-10">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-yellow-500/15 blur-[120px] rounded-full"></div>
                </div>

                <div className="flex items-center justify-center gap-3 mb-4">
                  <Star className="h-6 w-6 text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-wider">
                    FEATURED <span className="text-yellow-400">EXCLUSIVES</span>
                  </h2>
                  <Star className="h-6 w-6 text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
                </div>

                <div className="relative rounded-2xl overflow-hidden border-2 border-yellow-500/70 shadow-[0_0_40px_rgba(250,204,21,0.4)] bg-zinc-950">
                  <div className="aspect-video bg-black relative flex flex-col items-center justify-center px-6 text-center">
                    <Video className="h-16 w-16 text-yellow-400/80 mb-4 drop-shadow-[0_0_20px_rgba(250,204,21,0.6)]" />
                    <h3 className="text-2xl md:text-3xl font-black text-white tracking-wide mb-2">
                      DROP <span className="text-yellow-400">INCOMING</span>
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 max-w-md">
                      Exclusive footage is being prepped. Like, comment, share &amp; download tools
                      are armed and ready the moment we drop.
                    </p>
                  </div>
                  <div className="p-5 md:p-6">
                    <p className="text-xs md:text-sm text-gray-400">
                      Got something the mainstream won't touch?{' '}
                      <span className="text-red-400 font-bold">Submit a tip</span> using the floating
                      button - anonymous, encrypted, raw.
                    </p>
                  </div>
                </div>
              </section>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="explicit-video-grid">
                {videos.map((video, idx) => (
                  <React.Fragment key={video.id}>
                    <VideoCard video={video} />
                    {/* Inline ad every 4 videos */}
                    {((idx + 1) % 4 === 0) && (idx < videos.length - 1) && (
                      <div className="md:col-span-2 lg:col-span-3 -mx-4 sm:mx-0">
                        <AdSlot
                          slotId={`videos-inline-${Math.floor(idx / 4)}`}
                          className="py-2"
                          aspect="aspect-[5/1]"
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default ExplicitContent;
